import { useEffect } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Banner } from "@visioni/a3s-ui/components";
import { Text, View } from "@src/components";
import {
  LATEST_CLIENT_VERSION,
  parseClientVersionParam,
  useFleetClient,
  useFleetClientVersions,
  type ClientAddress,
  type ClientContact,
  type ClientDetail,
} from "@visioni/a3s-monitoring/app";
import { PatriotToolsPage } from "../components/PatriotToolsPage";
import { QueryNotice } from "../components/QueryNotice";
import { Empty, Field, InlineLink, Section } from "../components/DetailSection";
import { CursorPagerBar, countOf } from "../components/CursorPagerBar";
import { EMPTY, formatInstant, formatText, hasText } from "../format";
import {
  clientActivationsHref,
  clientIncidentsHref,
  clientOperatorNotesHref,
  clientSignalsHref,
  fleetClientHref,
} from "../routes";

/**
 * One Patriot client, as the replica holds it: who it is, how to reach the
 * premises, what operators are told to do and who they call.
 *
 * `?version=` says which version: `latest` for the client as it is now, or
 * the time one of its versions began, to see it as it was then. A link without
 * one is given `latest`, so the URL always says which the page shows.
 */
export function FleetClientScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ tenantId: string; clientNo?: string; version?: string }>();
  const { tenantId, clientNo = "" } = params;
  const version = parseClientVersionParam(params.version);

  useEffect(() => {
    if (version.kind === "unset") router.setParams({ version: LATEST_CLIENT_VERSION });
  }, [version.kind, router]);

  const validFrom = version.kind === "at" ? version.validFrom : undefined;
  const { client, isLoading, error, refetch } = useFleetClient(
    version.kind === "unset" || version.kind === "invalid" ? "" : clientNo,
    validFrom,
  );

  return (
    <PatriotToolsPage
      title={client !== undefined && hasText(client.name) ? client.name : `Patriot client ${clientNo}`}
      {...(client !== undefined && { subtitle: client.clientNo })}
    >
      {version.kind === "invalid" ?
        <View gap="lg">
          <Banner
            variant="warning"
            title="Not a version"
            message={`"${params.version ?? ""}" is not a time a version of this client began.`}
          />
          <InlineLink href={fleetClientHref(tenantId, clientNo, LATEST_CLIENT_VERSION)}>
            Show the latest version
          </InlineLink>
        </View>
      : client === undefined ?
        <QueryNotice isLoading={isLoading || version.kind === "unset"} error={error} onRetry={refetch} />
      : <View gap="4xl">
          {validFrom !== undefined && (
            <View gap="lg">
              <Banner
                variant="info"
                title="An earlier version"
                message={`This is the client as a3s copied it on ${formatInstant(validFrom)}, until its next version.`}
              />
              <InlineLink href={fleetClientHref(tenantId, clientNo, LATEST_CLIENT_VERSION)}>
                Show the latest version
              </InlineLink>
            </View>
          )}
          <ClientDetailView tenantId={tenantId} client={client} />
          <ClientVersions tenantId={tenantId} clientNo={clientNo} shown={validFrom} />
        </View>
      }
    </PatriotToolsPage>
  );
}

/**
 * The client's versions, newest first, each opening the page at that version.
 * The newest is the client as it is now, so it links to `latest`, which shows the same.
 */
function ClientVersions({
  tenantId,
  clientNo,
  shown,
}: {
  tenantId: string;
  clientNo: string;
  /** The version on screen; absent for the latest. */
  shown: string | undefined;
}) {
  const versions = useFleetClientVersions(clientNo);
  const newest = versions.pager.page === 1 ? versions.items?.[0]?.validFrom : undefined;

  return (
    <Section title="Versions">
      <Text textStyle="sm/regular" color="textMuted">
        Changes seen by a3s.
      </Text>
      {versions.items === undefined || versions.error !== null ?
        <QueryNotice isLoading={versions.isLoading} error={versions.error} onRetry={versions.refetch} />
      : versions.items.length === 0 ?
        <Empty>No changes seen yet.</Empty>
      : <View gap="sm">
          {versions.items.map(({ validFrom }) => {
            const isNewest = validFrom === newest;
            const isShown = shown === undefined ? isNewest : validFrom === shown;
            const label = `${formatInstant(validFrom)}${isNewest ? " (latest)" : ""}`;
            return isShown ?
                <Text key={validFrom} textStyle="sm/semibold" color="text">
                  {`${label} — shown`}
                </Text>
              : <InlineLink
                  key={validFrom}
                  href={fleetClientHref(tenantId, clientNo, isNewest ? LATEST_CLIENT_VERSION : validFrom)}
                >
                  {label}
                </InlineLink>;
          })}
          <CursorPagerBar
            pager={versions.pager}
            nextCursor={versions.nextCursor}
            isFetching={versions.isFetching}
            summary={countOf(versions.items.length, "version")}
          />
        </View>
      }
    </Section>
  );
}

function ClientDetailView({ tenantId, client }: { tenantId: string; client: ClientDetail }) {
  return (
    <View gap="4xl">
      <Section title="Summary">
        <Field label="Client">{client.clientNo}</Field>
        <Field label="Name">{formatText(client.name)}</Field>
        <Field label="Description">{formatText(client.description)}</Field>
        <Field label="Attend alarms">
          {client.attendAlarm === undefined ?
            EMPTY
          : client.attendAlarm ?
            "Yes"
          : "No"}
        </Field>
      </Section>

      {/* Patriot's own names, so support can find the same values in Patriot. */}
      <Section title="Patriot identity">
        <Field label="External ref no.">
          {client.externalRefNo === undefined ? EMPTY : String(client.externalRefNo)}
        </Field>
        <Field label="Client ID">{formatText(client.clientID)}</Field>
        <Field label="Area no.">{formatText(client.areaNo)}</Field>
        <Field label="Port ID">{formatText(client.portID)}</Field>
      </Section>

      <Section title="Related">
        <InlineLink href={clientActivationsHref(tenantId, client.clientNo)}>Activations for this client</InlineLink>
        <InlineLink href={clientIncidentsHref(tenantId, client.clientNo)}>Genuine incidents for this client</InlineLink>
        <InlineLink href={clientSignalsHref(tenantId, client.clientNo)}>Signals for this client</InlineLink>
        <InlineLink href={clientOperatorNotesHref(tenantId, client.clientNo)}>
          Operator notes for this client
        </InlineLink>
      </Section>

      <Section title="Premises">
        <Field label="Address">{formatAddress(client.address)}</Field>
      </Section>

      <Section title="Response instructions">
        {hasText(client.responseInstructions) ?
          // Free text written for the operator; its line breaks are its layout.
          <Text textStyle="sm/regular" color="text" selectable>
            {client.responseInstructions}
          </Text>
        : <Empty>No response instructions.</Empty>}
      </Section>

      <Section title={`Contacts (${String(client.contacts.length)})`}>
        {client.contacts.length === 0 ?
          <Empty>No contacts.</Empty>
        : client.contacts.map((contact, index) => (
            <View key={contact.clientUserId} style={[styles.entry, index > 0 && styles.entryDivider]}>
              <Field label="Name">{formatContactName(contact)}</Field>
              <Field label="Type">{formatText(contact.type)}</Field>
              <Field label="Call list">
                {contact.callListPosition === undefined ?
                  "Not on the call list"
                : `#${String(contact.callListPosition)}`}
              </Field>
              {contact.numbers.length === 0 ?
                <Field label="Numbers">{EMPTY}</Field>
              : contact.numbers.map((number) => (
                  <Field key={number.kind} label={NUMBER_LABELS[number.kind]}>
                    {number.number}
                  </Field>
                ))
              }
              {hasText(contact.email) && <Field label="Email">{contact.email}</Field>}
              {hasText(contact.mobileEmail) && <Field label="Mobile email">{contact.mobileEmail}</Field>}
              {hasText(contact.instructions) && <Field label="Instructions">{contact.instructions}</Field>}
            </View>
          ))
        }
      </Section>

      <Section title="Copy in a3s">
        <Field label="First copied">{formatInstant(client.a3sFirstSeenAt)}</Field>
        <Field label="Last changed">{formatInstant(client.a3sLastWrittenAt)}</Field>
      </Section>
    </View>
  );
}

const NUMBER_LABELS: Record<ClientContact["numbers"][number]["kind"], string> = {
  mobile: "Mobile",
  phone: "Phone",
  afterHours: "After hours",
  pager: "Pager",
};

function formatContactName(contact: ClientContact): string {
  return hasText(contact.name) ? contact.name : `User ${String(contact.userNo)}`;
}

function formatAddress(address: ClientAddress | undefined): string {
  if (address === undefined) return EMPTY;
  const locality = [address.city, address.state, address.postcode].filter(hasText).join(" ");
  const lines = [...address.addressLines, locality, address.country].filter(hasText);
  return lines.length === 0 ? EMPTY : lines.join("\n");
}

const styles = StyleSheet.create((theme) => ({
  entry: { gap: theme.spacing.xs, paddingTop: theme.spacing.md },
  entryDivider: { borderTopWidth: 1, borderColor: theme.colors.borderCard },
}));
